/**
 * ThemedSelect Component
 * 
 * Select/dropdown that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedSelectProps, ThemedSelectOption } from '../../types/immersive-theme.types';
import {
  neumorphismSelect,
  neumorphismSelectDropdown,
  neumorphismSelectOption,
  neumorphismSelectOptionHover,
} from './theme-styles/neumorphism';
import {
  brutalistSelect,
  brutalistSelectDropdown,
  brutalistSelectOption,
  brutalistSelectOptionHover,
  brutalistSelectOptionLast,
} from './theme-styles/brutalist';
import {
  retroTerminalSelect,
  retroTerminalSelectDropdown,
  retroTerminalSelectOption,
  retroTerminalSelectOptionHover,
  retroTerminalSelectOptionPrefix,
} from './theme-styles/retro-terminal';
import {
  organicNatureSelect,
  organicNatureSelectDropdown,
  organicNatureSelectOption,
  organicNatureSelectOptionHover,
  organicNatureSelectOptionSelected,
} from './theme-styles/organic-nature';
import {
  luxuryGoldSelect,
  luxuryGoldSelectDropdown,
  luxuryGoldSelectOption,
  luxuryGoldSelectOptionHover,
  luxuryGoldSelectOptionSelected,
} from './theme-styles/luxury-gold';

// Select trigger styles per theme
const selectStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    padding: '12px 40px 12px 16px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    background: '#ffffff',
    cursor: 'pointer',
    appearance: 'none',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismSelect,
  [ImmersiveThemeType.BRUTALIST]: brutalistSelect,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalSelect,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureSelect,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldSelect,
};

// Dropdown styles per theme
const dropdownStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    marginTop: '4px',
    overflow: 'hidden',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismSelectDropdown,
  [ImmersiveThemeType.BRUTALIST]: brutalistSelectDropdown,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalSelectDropdown,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureSelectDropdown,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldSelectDropdown,
};

// Option styles per theme
const optionStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismSelectOption,
  [ImmersiveThemeType.BRUTALIST]: brutalistSelectOption,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalSelectOption,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureSelectOption,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldSelectOption,
};

// Option hover styles per theme
const optionHoverStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { background: '#f3f4f6' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismSelectOptionHover,
  [ImmersiveThemeType.BRUTALIST]: brutalistSelectOptionHover,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalSelectOptionHover,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureSelectOptionHover,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldSelectOptionHover,
};

// Size styles
const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { padding: '8px 36px 8px 12px', fontSize: '13px' },
  md: { padding: '12px 40px 12px 16px', fontSize: '14px' },
  lg: { padding: '16px 44px 16px 20px', fontSize: '16px' },
};

// Chevron icon component
const ChevronIcon: React.FC<{ isOpen: boolean; theme: ImmersiveThemeType }> = ({ isOpen, theme }) => {
  const iconColor = theme === ImmersiveThemeType.RETRO_TERMINAL 
    ? 'var(--term-text)' 
    : theme === ImmersiveThemeType.LUXURY_GOLD 
      ? 'var(--lux-gold-solid)' 
      : 'currentColor';

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={{
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
      }}
    >
      <path
        d="M4 6L8 10L12 6"
        stroke={iconColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ThemedSelect: React.FC<ThemedSelectProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  error,
  label,
  helperText,
  size = 'md',
  fullWidth = false,
  searchable = false,
  clearable = false,
  className,
  name,
  id,
  required,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update selected value when prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Get selected option
  const selectedOption = options.find((opt) => opt.value === selectedValue);

  // Handle option selection
  const handleSelect = useCallback((option: ThemedSelectOption) => {
    if (option.disabled) return;
    setSelectedValue(option.value);
    onChange?.(option.value);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
    }
  }, [disabled, isOpen, highlightedIndex, filteredOptions, handleSelect]);

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue('');
    onChange?.('');
  };

  // Compute styles
  const triggerStyles: React.CSSProperties = {
    ...selectStyles[theme],
    ...sizeStyles[size],
    ...(fullWidth ? { width: '100%' } : {}),
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
    ...(error ? { borderColor: 'var(--color-danger, #ef4444)' } : {}),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const dropdownComputedStyles: React.CSSProperties = {
    ...dropdownStyles[theme],
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 50,
    maxHeight: '240px',
    overflowY: 'auto',
    ...(reducedMotion ? { transition: 'none' } : {}),
  };

  // Label styles based on theme
  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    display: 'block',
    ...(theme === ImmersiveThemeType.LUXURY_GOLD ? { color: 'var(--lux-text-gold)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '12px' } : {}),
    ...(theme === ImmersiveThemeType.BRUTALIST ? { textTransform: 'uppercase', fontWeight: 900 } : {}),
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { fontFamily: 'var(--term-font)', textShadow: 'var(--term-text-shadow)' } : {}),
  };

  // Helper text styles
  const helperStyles: React.CSSProperties = {
    fontSize: '12px',
    marginTop: '6px',
    color: error ? 'var(--color-danger, #ef4444)' : 'var(--color-text-secondary, #6b7280)',
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { fontFamily: 'var(--term-font)', textShadow: error ? '0 0 10px #ff4444' : 'var(--term-text-shadow)' } : {}),
  };

  // Get display text
  const getDisplayText = () => {
    if (selectedOption) {
      return theme === ImmersiveThemeType.RETRO_TERMINAL
        ? `> ${selectedOption.label}`
        : selectedOption.label;
    }
    return theme === ImmersiveThemeType.RETRO_TERMINAL
      ? `> ${placeholder}`
      : placeholder;
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}
      className={className}
    >
      {label && (
        <label htmlFor={id || name} style={labelStyles}>
          {label}
          {required && <span style={{ color: 'var(--color-danger, #ef4444)', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${id || name}-listbox`}
        aria-labelledby={label ? `${id || name}-label` : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        style={triggerStyles}
      >
        <span style={{ 
          flex: 1, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          color: selectedOption ? 'inherit' : 'var(--color-text-secondary, #9ca3af)',
        }}>
          {getDisplayText()}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {clearable && selectedValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Limpar seleção"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <ChevronIcon isOpen={isOpen} theme={theme} />
        </div>
      </div>

      {isOpen && (
        <div
          id={`${id || name}-listbox`}
          role="listbox"
          style={dropdownComputedStyles}
        >
          {searchable && (
            <div style={{ padding: '8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                  ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { 
                    background: 'var(--term-bg)', 
                    color: 'var(--term-text)', 
                    border: 'var(--term-border)',
                    fontFamily: 'var(--term-font)',
                  } : {}),
                }}
              />
            </div>
          )}

          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: 'var(--color-text-secondary, #9ca3af)', textAlign: 'center' }}>
              {theme === ImmersiveThemeType.RETRO_TERMINAL ? '> NO RESULTS FOUND' : 'Nenhum resultado encontrado'}
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = option.value === selectedValue;
              const isHighlighted = index === highlightedIndex;
              const isLast = index === filteredOptions.length - 1;

              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{
                    ...optionStyles[theme],
                    ...(isHighlighted || isSelected ? optionHoverStyles[theme] : {}),
                    ...(isSelected && theme === ImmersiveThemeType.ORGANIC_NATURE ? organicNatureSelectOptionSelected : {}),
                    ...(isSelected && theme === ImmersiveThemeType.LUXURY_GOLD ? luxuryGoldSelectOptionSelected : {}),
                    ...(isLast && theme === ImmersiveThemeType.BRUTALIST ? brutalistSelectOptionLast : {}),
                    ...(option.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {theme === ImmersiveThemeType.RETRO_TERMINAL && (
                    <span style={retroTerminalSelectOptionPrefix}>{isSelected ? '[x]' : '[ ]'}</span>
                  )}
                  {option.icon && <span>{option.icon}</span>}
                  <span>{option.label}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      {(helperText || error) && (
        <span style={helperStyles}>
          {error || helperText}
        </span>
      )}

      {/* Hidden native select for form submission */}
      <select
        name={name}
        id={id}
        value={selectedValue}
        onChange={(e) => {
          setSelectedValue(e.target.value);
          onChange?.(e.target.value);
        }}
        required={required}
        disabled={disabled}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ThemedSelect;
